import { Link } from "react-router-dom";
import { useEffect } from "react";
import Footer from "@/components/Footer";
import Seo from "@/lib/seo";

const CONTACT = process.env.REACT_APP_CONTACT_EMAIL || "hello@barrettcareerbrand.com.au";

function LegalLayout({ title, description, path, children }) {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <main className="bg-[#0A0A0A] text-[#F8F8F5] min-h-screen">
      <Seo title={`${title} — Barrett Career Brand`} description={description} path={path} />
      <div className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center">
          <Link to="/" className="font-display text-xl font-bold tracking-tighter text-[#F8F8F5]">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
        </div>
      </div>
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <h1 className="font-display font-bold tracking-tighter text-4xl md:text-5xl mb-4">{title}</h1>
        <p className="font-body text-sm text-[#52525B] mb-10">Last updated: {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long" })}</p>
        <div className="space-y-6 font-body text-[#A1A1AA] leading-relaxed [&_h2]:font-display [&_h2]:text-[#F8F8F5] [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_a]:text-[#DFFF00] [&_strong]:text-[#F8F8F5]">
          {children}
        </div>
        <div className="mt-14"><Link to="/" className="text-[#DFFF00] font-body font-semibold">← Back to home</Link></div>
      </article>
      <Footer />
    </main>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" description="How Barrett Career Brand collects, uses and protects your personal information." path="/privacy">
      <p>This Privacy Policy explains how Barrett Career Brand (&quot;we&quot;, &quot;us&quot;) handles personal information in line with Australian privacy principles. This page is provided for transparency and is not legal advice; it has not been verified by a lawyer.</p>
      <h2>Information we collect</h2>
      <p>We may collect your name, email address, current and target roles, the contents of any CV you upload or paste, and details you provide in forms or consultations.</p>
      <h2>Why we collect it</h2>
      <p>To provide your Career Brand Score and report, respond to enquiries, deliver services you purchase, and — only with your consent — send occasional career-branding advice.</p>
      <h2>Marketing consent</h2>
      <p>We only send marketing emails if you tick the consent box. You can unsubscribe at any time using the link in any marketing email or by contacting us.</p>
      <h2>Data sharing</h2>
      <p>We use trusted third-party providers to run our service, including email delivery, payment processing and website hosting. We do not sell your personal information.</p>
      <h2>Retention & deletion</h2>
      <p>We retain your information only for as long as necessary to provide our services and meet our legal obligations, after which it is securely deleted. You can request access to, correction of, or deletion of your personal information at any time by emailing <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
      <h2>Contact</h2>
      <p>Questions about privacy? Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" description="The terms that apply when you use Barrett Career Brand's website and services." path="/terms">
      <p>These terms apply to your use of this website and our services. This page is provided for transparency and is not legal advice; it has not been verified by a lawyer.</p>
      <h2>Our services</h2>
      <p>We provide CV writing and editing, LinkedIn optimisation and career-branding support. The free Career Brand Score is an automated, AI-supported assessment intended as guidance, not a guarantee of employment outcomes.</p>
      <h2>Your responsibilities</h2>
      <p>You agree to provide accurate information and to hold the rights to any content you share with us.</p>
      <h2>Payments</h2>
      <p>Paid services are billed in Australian dollars (AUD) through our payment provider. The scope and price are agreed with you in a written quote before any work begins.</p>
      <h2>No guarantee of outcomes</h2>
      <p>We help you present your experience effectively, but we cannot guarantee interviews, offers or specific results.</p>
      <h2>Governing law</h2>
      <p>These terms are governed by the laws of Australia. If you have questions about our business details or a specific service, please contact us.</p>
      <h2>Contact</h2>
      <p>Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
    </LegalLayout>
  );
}

export function Refunds() {
  return (
    <LegalLayout title="Refund & Cancellation Policy" description="How refunds and cancellations work for Barrett Career Brand services." path="/refunds">
      <p>We want you to be happy with your service. This page describes our approach to refunds and cancellations. It is provided for transparency and is not legal advice.</p>
      <h2>Before work begins</h2>
      <p>If you cancel before we begin work on your order, you may be eligible for a full refund. Please email us as soon as possible so we can confirm your order status.</p>
      <h2>After work begins</h2>
      <p>Because our services involve personalised work, refunds after work has commenced are handled case by case. Where work has partially begun, any refund reflects the work already completed, and we&apos;ll always discuss this openly with you.</p>
      <h2>Australian Consumer Law</h2>
      <p>Nothing in this policy limits your rights under the Australian Consumer Law.</p>
      <h2>Requesting a refund</h2>
      <p>Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a> with your order details.</p>
    </LegalLayout>
  );
}

export function DataHandling() {
  return (
    <LegalLayout title="CV Upload & AI Data-Handling Information" description="What happens to your CV when you use the free Career Brand Score, and how AI is used." path="/data-handling">
      <p>This page explains what happens when you upload or paste a CV for the free Career Brand Score.</p>
      <h2>What we collect</h2>
      <p>The text of the CV you upload or paste, plus your first name and email if you request the full report.</p>
      <h2>Why we collect it</h2>
      <p>To generate your Career Brand Score and, if you request it, email your detailed report and action plan.</p>
      <h2>Is AI used?</h2>
      <p><strong>Yes.</strong> Your CV text is analysed using AI-supported tools to produce the assessment. AI supports the process; George reviews and shapes recommendations for paid work. You remain the source of truth about your experience.</p>
      <h2>Are files retained?</h2>
      <p><strong>Yes.</strong> If you upload a CV document (PDF, DOCX or TXT), the original file is securely retained and linked to your assessment. It is held in private, access-controlled storage and is accessible only to George Barrett (the site owner) through an authenticated admin area. It is never shared publicly, attached to automated emails, or exported to spreadsheets — those contain only a reference to your assessment. If you paste your CV as text instead of uploading a file, no document is stored — only your assessment results. Assessment results are stored so we can send your report.</p>
      <h2>How long is information kept?</h2>
      <p>We keep scores, reports and enquiry details only for as long as necessary to provide our services and meet legal obligations, after which they are securely deleted. You can request earlier deletion at any time.</p>
      <h2>Requesting deletion</h2>
      <p>Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a> to request deletion of your information — including your uploaded CV document — at any time. We will confirm once it has been removed.</p>
      <h2>Sharing with providers</h2>
      <p>We use third-party providers for AI analysis, email delivery and website hosting. We do not sell your information.</p>
      <h2>Contact</h2>
      <p>Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
    </LegalLayout>
  );
}

export function Contact() {
  return (
    <LegalLayout title="Contact" description="Get in touch with Barrett Career Brand." path="/contact">
      <p>We&apos;d love to hear from you.</p>
      <h2>Email</h2>
      <p><a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
      <h2>Free consultation</h2>
      <p>Prefer to talk? <Link to="/#consultation">Request a free 15-minute consultation.</Link></p>
    </LegalLayout>
  );
}
